import React from "react";
import { Box, Flex, Text, Link, Icon } from "@chakra-ui/react";
import { FaQuoteLeft } from "react-icons/fa";
import { FiArrowUpRight } from "react-icons/fi";
import { BsLinkedin } from "react-icons/bs";
import { ChapterHeading, Reveal, MotionBox, glass } from "./story";

const RECOMMENDATIONS_URL =
  "https://www.linkedin.com/in/mukul-jatav/details/recommendations/";
const FOUNDER_POST_URL =
  "https://www.linkedin.com/feed/update/urn:li:share:7283722320538472448/";
const FOUNDER_POST_EMBED =
  "https://www.linkedin.com/embed/feed/update/urn:li:share:7283722320538472448?collapsed=1";

/**
 * Recommendations pulled from Mukul's LinkedIn profile.
 * The quotes are reproduced WORD FOR WORD as their authors wrote them, so they
 * keep the original punctuation. Do not reword them. Cards show an excerpt and
 * link out to the full note on LinkedIn.
 */
const quotes = [
  {
    name: "Yash Jain",
    role: "Software Development Engineer III",
    relation: "My team lead at Salesken",
    text: `I had the pleasure of working with Mukul and was always impressed by his strong technical expertise and problem-solving mindset. With solid skills across Next.js, React.js, Node.js, and cloud technologies, he consistently delivers scalable and efficient solutions. What truly stands out is Mukul's ability to quickly grasp complex requirements and translate them into reliable, well-structured implementations. Beyond his technical strengths, Mukul is a collaborative and dependable teammate who brings positivity and focus to the workplace.`,
  },
  {
    name: "Rajan Priyadarshi",
    role: "SDE3 at Salesken.ai",
    relation: "Same project, different team",
    text: `During his internship, Mukul showcased exceptional skills as a Software Development Engineer Intern, he consistently delivered high-quality work, taking ownership of critical projects and collaborating effectively with the team. His attention to detail, problem-solving capabilities, and ability to quickly adapt to new challenges made him an asset to our team.`,
  },
];

/** Small, high intent call to action button. */
function CtaButton({ href, children }) {
  return (
    <Link href={href} isExternal _hover={{ textDecoration: "none" }} w="fit-content">
      <Flex
        align="center"
        gap="7px"
        bg="rgba(96,165,250,0.12)"
        border="1px solid rgba(96,165,250,0.35)"
        color="#93C5FD"
        px="14px"
        py="8px"
        borderRadius="8px"
        fontWeight="700"
        fontSize="xs"
        transition="all 0.2s"
        _hover={{ bg: "#60A5FA", color: "white", borderColor: "#60A5FA" }}
      >
        {children}
        <Icon as={FiArrowUpRight} boxSize="14px" />
      </Flex>
    </Link>
  );
}

export default function Recommendations() {
  return (
    <Box
      id="Words"
      minH={{ lg: "100vh" }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      py={{ base: "70px", lg: "56px" }}
      px={{ base: "6%", md: "9%" }}
    >
      <ChapterHeading
        compact
        chapter="Chapter 07 · In Their Words"
        title="What the people I've worked with say"
        subtitle="Straight from my LinkedIn profile."
      />

      <Flex
        maxW="1180px"
        mx="auto"
        w="100%"
        gap="20px"
        direction={{ base: "column", lg: "row" }}
        align="stretch"
      >
        {/* written recommendations */}
        <Flex direction="column" gap="20px" flex="1">
          {quotes.map((q, i) => (
            <Reveal key={q.name} delay={i * 0.1} style={{ display: "flex", flex: 1 }}>
              <MotionBox
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 250 }}
                {...glass({ p: { base: "18px", md: "20px" } })}
                display="flex"
                flexDirection="column"
                w="100%"
                _hover={{ borderColor: "rgba(96,165,250,0.45)" }}
              >
                <Icon as={FaQuoteLeft} boxSize="15px" color="#60A5FA" opacity={0.7} mb="10px" />

                <Text color="#c2c9d6" fontSize="sm" lineHeight="1.75" noOfLines={3}>
                  {q.text}
                </Text>

                <Flex mt="14px" align="center" gap="12px">
                  <Flex
                    align="center"
                    justify="center"
                    w="38px"
                    h="38px"
                    borderRadius="full"
                    bg="rgba(96,165,250,0.14)"
                    color="#60A5FA"
                    fontWeight="800"
                    fontSize="xs"
                    flexShrink={0}
                  >
                    {q.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </Flex>
                  <Box flex="1" minW={0}>
                    <Text fontWeight="800" color="#f0f2f7" fontSize="sm">
                      {q.name}
                    </Text>
                    <Text color="#98a2b8" fontSize="xs" noOfLines={1}>
                      {q.role}
                    </Text>
                    <Text color="#60A5FA" fontSize="10px" noOfLines={1} mt="1px">
                      {q.relation}
                    </Text>
                  </Box>
                  <CtaButton href={RECOMMENDATIONS_URL}>Read full note</CtaButton>
                </Flex>
              </MotionBox>
            </Reveal>
          ))}
        </Flex>

        {/* the founder's post, embedded live from LinkedIn.
            Stretches to match the height of the recommendation column. */}
        <Box w={{ base: "100%", lg: "460px" }} flexShrink={0} display="flex">
          <Reveal delay={0.15} style={{ display: "flex", flex: 1, width: "100%" }}>
            <Box
              {...glass({ p: { base: "14px", md: "16px" } })}
              display="flex"
              flexDirection="column"
              w="100%"
            >
              <Flex align="center" justify="space-between" mb="12px" gap="10px">
                <Flex align="center" gap="8px" minW={0}>
                  <Icon as={BsLinkedin} boxSize="14px" color="#60A5FA" flexShrink={0} />
                  <Box minW={0}>
                    <Text
                      fontSize="xs"
                      fontWeight="700"
                      color="#60A5FA"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      noOfLines={1}
                    >
                      Siddharth Kothari
                    </Text>
                    <Text color="#98a2b8" fontSize="10px" noOfLines={1}>
                      Founder of Workwise, where I build today
                    </Text>
                  </Box>
                </Flex>
                <CtaButton href={FOUNDER_POST_URL}>Read post</CtaButton>
              </Flex>
              <Box
                as="iframe"
                src={FOUNDER_POST_EMBED}
                title="Founder post about Mukul on LinkedIn"
                w="100%"
                flex="1"
                minH={{ base: "330px", lg: "336px" }}
                border="0"
                borderRadius="10px"
                allowFullScreen
                loading="lazy"
                display="block"
              />
            </Box>
          </Reveal>
        </Box>
      </Flex>
    </Box>
  );
}
